import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../config/api';
import { extractErrorMessage } from '../utils/extractError';

const ROLE_OPTIONS = [
  { value: 'cashier', label: 'Cashier' },
  { value: 'shift_lead', label: 'Shift Lead' },
  { value: 'stock_clerk', label: 'Stock Clerk' },
  { value: 'manager', label: 'Manager' },
];

export default function AddEditEmployeePage() {
  const { storeId, employeeId } = useParams<{ storeId?: string; employeeId?: string }>();
  const navigate = useNavigate();

  const isEdit = Boolean(employeeId);

  const [name, setName] = useState('');
  const [role, setRole] = useState('cashier');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    async function fetchEmployee() {
      try {
        const res = await api.get(`/employees/${employeeId}`);
        if (!cancelled) {
          setName(res.data.full_name || '');
          setRole(res.data.role || 'cashier');
          setPhone(res.data.phone || '');
          setLoading(false);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(extractErrorMessage(err, 'Failed to load employee.'));
          setLoading(false);
        }
      }
    }
    fetchEmployee();
    return () => { cancelled = true; };
  }, [isEdit, employeeId]);

  function validatePhone(value: string): boolean {
    if (!value.trim()) return true; // optional
    const digits = value.replace(/\D/g, '');
    return digits.length >= 10;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    if (!validatePhone(phone)) {
      setError('Phone number must have at least 10 digits.');
      return;
    }

    setSubmitting(true);
    setError('');

    const payload: Record<string, any> = {
      full_name: name.trim(),
      role,
    };
    if (phone.trim()) payload.phone = phone.trim();

    try {
      if (isEdit) {
        await api.put(`/employees/${employeeId}`, payload);
      } else {
        await api.post(`/stores/${storeId}/employees`, payload);
      }
      navigate(-1);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to save employee.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={styles.page}>
      <main style={styles.main}>
        <button onClick={() => navigate(-1)} style={styles.backButton}>
          ← Back
        </button>

        <div style={styles.card}>
          <h2 style={styles.cardTitle}>{isEdit ? 'Edit Employee' : 'Add Employee'}</h2>

          {loading ? (
            <p style={styles.statusText}>Loading...</p>
          ) : (
            <>
              {error && <div style={styles.errorBanner}>{error}</div>}

              <form onSubmit={handleSubmit} style={styles.form}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Name *</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Full name"
                    disabled={submitting}
                    required
                    style={styles.input}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Role *</label>
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    disabled={submitting}
                    style={styles.select}
                  >
                    {ROLE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Phone</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(555) 123-4567"
                    disabled={submitting}
                    style={styles.input}
                  />
                  <span style={styles.hint}>At least 10 digits if provided</span>
                </div>

                <div style={styles.actions}>
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    disabled={submitting}
                    style={styles.cancelButton}
                  >
                    Cancel
                  </button>
                  <button type="submit" disabled={submitting} style={styles.submitButton}>
                    {submitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Employee'}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { minHeight: '100vh', background: 'var(--bg-base)' },
  main: { maxWidth: 520, margin: '0 auto', padding: 'var(--space-8) var(--space-6)' },
  backButton: {
    background: 'none', border: 'none', color: 'var(--accent-text)',
    fontSize: 'var(--text-md)', fontWeight: 500, fontFamily: 'var(--font-body)',
    cursor: 'pointer', padding: 0, marginBottom: 'var(--space-6)',
  },
  card: {
    background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)',
    border: '1px solid var(--border-subtle)', padding: '32px 32px 28px',
  },
  cardTitle: {
    fontSize: 'var(--text-xl)', fontWeight: 600, fontFamily: 'var(--font-display)',
    color: 'var(--text-primary)', margin: '0 0 24px',
  },
  statusText: { color: 'var(--text-secondary)', fontSize: 'var(--text-md)', fontFamily: 'var(--font-body)' },
  errorBanner: {
    background: 'var(--status-critical-muted)', color: 'var(--status-critical)',
    padding: '12px 16px', borderRadius: 'var(--radius-lg)', fontSize: 'var(--text-base)',
    fontFamily: 'var(--font-body)', border: '1px solid rgba(255,71,87,0.3)', marginBottom: 'var(--space-5)',
  },
  form: { display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 'var(--text-base)', fontWeight: 600, fontFamily: 'var(--font-body)', color: 'var(--text-secondary)' },
  input: {
    padding: '10px 14px', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-default)',
    fontSize: 'var(--text-md)', fontFamily: 'var(--font-body)', outline: 'none',
    background: 'var(--input-bg)', color: 'var(--text-primary)', colorScheme: 'dark',
    transition: 'border-color 0.2s',
  },
  select: {
    padding: '10px 14px', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-default)',
    fontSize: 'var(--text-md)', fontFamily: 'var(--font-body)', outline: 'none',
    background: 'var(--input-bg)', color: 'var(--text-primary)', cursor: 'pointer',
    appearance: 'auto' as any, colorScheme: 'dark', transition: 'border-color 0.2s',
  },
  hint: { fontSize: 'var(--text-sm)', fontFamily: 'var(--font-body)', color: 'var(--text-tertiary)' },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 8 },
  cancelButton: {
    padding: '10px 22px', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-default)',
    background: 'var(--bg-surface-raised)', fontSize: 'var(--text-md)', fontWeight: 500,
    fontFamily: 'var(--font-body)', color: 'var(--text-primary)', cursor: 'pointer',
  },
  submitButton: {
    padding: '10px 24px', borderRadius: 'var(--radius-lg)', border: 'none',
    background: 'var(--accent)', color: '#fff', fontSize: 'var(--text-md)',
    fontWeight: 600, fontFamily: 'var(--font-body)', cursor: 'pointer',
  },
};
